<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  addResource,
  LIMITS,
  RESOURCE_KINDS,
  type Resource,
  type ResourceKind,
} from '../model/spec'
import ResourceCard from './ResourceCard.vue'

const props = defineProps<{ modelValue: readonly Resource[] }>()
const emit = defineEmits<{ 'update:modelValue': [Resource[]] }>()

const { t } = useI18n()

const full = computed(() => props.modelValue.length >= LIMITS.resources)

/** 1-based position among lines of the same kind, for the remove buttons' names. */
const indexes = computed(() => {
  const seen = new Map<ResourceKind, number>()
  return props.modelValue.map((resource) => {
    const next = (seen.get(resource.kind) ?? 0) + 1
    seen.set(resource.kind, next)
    return next
  })
})

function replace(index: number, resource: Resource): void {
  emit(
    'update:modelValue',
    props.modelValue.map((line, at) => (at === index ? resource : line)),
  )
}

function remove(index: number): void {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, at) => at !== index),
  )
}

// The new card's first control takes focus, so a keyboard user lands where
// they will type next rather than back at the top of the list.
async function add(kind: ResourceKind): Promise<void> {
  const next = addResource(props.modelValue, kind)
  emit('update:modelValue', next)
  await nextTick()
  const added = next[next.length - 1]
  const control = added && document.querySelector<HTMLElement>(`[id^="r${added.id}-"]`)
  control?.focus()
}
</script>

<template>
  <section class="resources" aria-labelledby="resources-heading">
    <h2 id="resources-heading" class="resources__heading">{{ t('form.resources') }}</h2>

    <ResourceCard
      v-for="(resource, index) in modelValue"
      :key="resource.id"
      :model-value="resource"
      :index="indexes[index] ?? 1"
      @update:model-value="(value) => replace(index, value)"
      @remove="remove(index)"
    />

    <p v-if="modelValue.length === 0" class="resources__empty">{{ t('form.empty') }}</p>

    <div class="resources__add" role="group" :aria-label="t('form.add')">
      <button
        v-for="kind in RESOURCE_KINDS"
        :key="kind"
        type="button"
        :disabled="full"
        @click="add(kind)"
      >
        <span aria-hidden="true">+</span> {{ t(`kind.${kind}`) }}
      </button>
    </div>
    <p v-if="full" class="resources__empty" role="status">{{ t('form.addLimit') }}</p>
  </section>
</template>

<style scoped>
.resources {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.resources__heading {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
}

.resources__empty {
  margin: 0;
  color: var(--color-text-muted);
}

.resources__add {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.resources__add button {
  padding: var(--space-2) var(--space-3);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-accent);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
}

.resources__add button:hover:not(:disabled) {
  border-style: solid;
  border-color: var(--color-accent);
  background: var(--color-accent-subtle);
}

.resources__add button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
